/**
 * Zapier-style searchable event picker for trigger/action selection.
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import { makeStyles, fade } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => {
  const hoverBg = fade(theme.palette.primary.main, theme.palette.type === 'dark' ? 0.16 : 0.08);
  return {
    dialogPaper: {
      borderRadius: 12,
      maxHeight: '80vh',
      width: '100%',
      maxWidth: 520,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingRight: theme.spacing(1),
    },
    searchField: {
      marginBottom: theme.spacing(1.5),
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      maxHeight: '55vh',
      overflowY: 'auto',
    },
    eventRow: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 4,
      width: '100%',
      border: 'none',
      background: 'transparent',
      borderRadius: 8,
      padding: theme.spacing(1.25, 1.5),
      cursor: 'pointer',
      font: 'inherit',
      color: theme.palette.text.primary,
      textAlign: 'left',
      transition: 'background-color 0.12s',
      '&:hover': {
        backgroundColor: hoverBg,
      },
    },
    eventRowSelected: {
      backgroundColor: hoverBg,
    },
    eventTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
    },
    eventTitle: {
      fontSize: '0.95rem',
      fontWeight: 700,
    },
    eventDesc: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
    },
    badge: {
      height: 22,
      fontSize: '0.7rem',
      fontWeight: 600,
    },
    empty: {
      padding: theme.spacing(3, 1),
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    loadingWrap: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
  };
});

/**
 * @typedef {object} EventOption
 * @property {string} key
 * @property {string} title
 * @property {string} [description]
 * @property {string} [badge]
 * @property {object} [raw] - original schema entry for callers
 */

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(event: EventOption) => void} props.onSelect
 * @param {EventOption[]} props.events
 * @param {boolean} [props.loading]
 * @param {string} [props.selectedKey]
 * @param {string} [props.title]
 * @param {string} [props.searchPlaceholder]
 */
const EventPickerModal = ({
  open,
  onClose,
  onSelect,
  events = [],
  loading = false,
  selectedKey = '',
  title = 'Choose an event',
  searchPlaceholder = 'Search events…',
}) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return events;
    const q = query.toLowerCase().trim();
    return events.filter(
      (e) =>
        (e.title && e.title.toLowerCase().includes(q)) ||
        (e.key && e.key.toLowerCase().includes(q)) ||
        (e.description && e.description.toLowerCase().includes(q)) ||
        (e.badge && e.badge.toLowerCase().includes(q))
    );
  }, [events, query]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography>
        <Box className={classes.titleRow}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          className={classes.searchField}
          fullWidth
          variant="outlined"
          size="small"
          autoFocus
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography className={classes.empty}>
            {query ? `No events match "${query}"` : 'No events available for this app'}
          </Typography>
        ) : (
          <Box className={classes.list}>
            {filtered.map((event) => {
              const selected = selectedKey && event.key === selectedKey;
              return (
                <button
                  key={event.key}
                  type="button"
                  className={`${classes.eventRow}${selected ? ` ${classes.eventRowSelected}` : ''}`}
                  onClick={() => {
                    onSelect(event);
                    onClose();
                  }}
                >
                  <Box className={classes.eventTitleRow}>
                    <Typography className={classes.eventTitle}>{event.title}</Typography>
                    {event.badge ? (
                      <Chip size="small" label={event.badge} className={classes.badge} color="primary" variant="outlined" />
                    ) : null}
                  </Box>
                  {event.description ? (
                    <Typography className={classes.eventDesc}>{event.description}</Typography>
                  ) : null}
                </button>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventPickerModal;
